import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Col, Row, Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Block, BlockHead, BlockHeadContent, BlockTitle, BlockDes, Icon } from "../../components/Component";
import { handleGetApis, handlePostApis } from "../../service/Api";
import RichTextEditor from "../../components/content/RichTextEditor";
import SeoFields from "../../components/content/SeoFields";
import ImageField from "../../components/content/ImageField";

const STOREFRONT = (process.env.REACT_APP_STOREFRONT_URL || "https://ryzhm.com").replace(/\/$/, "");

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  category: "",
  author_name: "",
  author_image: "",
  product_id: "",
  cover_image: "",
  body: "",
  meta_title: "",
  meta_description: "",
  is_published: false,
  published_at: "",
};

// MySQL DATETIME → value for <input type="datetime-local"> (local time).
const toLocalInput = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toForm = (p) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])),
  is_published: !!p.is_published,
  published_at: toLocalInput(p.published_at),
  product_id: p.product_id ? String(p.product_id) : "",
});

const JournalEditor = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(isNew ? EMPTY : null);
  const [savedSlug, setSavedSlug] = useState("");
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [products, setProducts] = useState([]);

  // Products for the "featured product" picker (id + name only).
  useEffect(() => {
    let cancelled = false;
    handleGetApis("admin/get/products").then((res) => {
      if (cancelled || !res?.data?.success) return;
      setProducts((res.data.data ?? []).map(({ id, name, is_active }) => ({ id, name, is_active })));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    handleGetApis(`admin/journal/${id}`).then((res) => {
      if (cancelled) return;
      if (res?.data?.success) {
        setForm(toForm(res.data.data));
        setSavedSlug(res.data.data.slug);
        setEditorKey((k) => k + 1);
      } else {
        setLoadError(res?.data?.message || "Couldn't load this article.");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async (publish) => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.body.trim()) return toast.error("Write the article before saving");
    setSaving(true);
    const payload = {
      ...form,
      id: isNew ? undefined : Number(id),
      is_published: publish,
      // datetime-local is local time; send an ISO instant.
      published_at: form.published_at ? new Date(form.published_at).toISOString() : undefined,
    };
    const res = await handlePostApis("admin/journal", payload);
    setSaving(false);
    if (!res?.data?.success) return toast.error(res?.data?.message || "Couldn't save.");

    const saved = res.data.data;
    setForm(toForm(saved));
    setSavedSlug(saved.slug);
    toast.success(publish ? "Published — live within a few seconds." : "Draft saved.");
    if (isNew) navigate(`/journal/${saved.id}`, { replace: true });
  };

  if (loadError) {
    return (
      <Content>
        <p className="text-danger">{loadError}</p>
        <Link to="/journal">← Back to Journal</Link>
      </Content>
    );
  }
  if (!form) {
    return (
      <Content>
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      </Content>
    );
  }

  const path = `/journal/${savedSlug || form.slug || "…"}`;

  return (
    <React.Fragment>
      <Head title={isNew ? "New article" : "Edit article"} />
      <ToastContainer position="top-right" autoClose={3000} />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <Link to="/journal" className="text-soft fs-13px">
              ← Journal
            </Link>
            <BlockTitle page>{isNew ? "New article" : form.title || "Edit article"}</BlockTitle>
            {savedSlug && form.is_published && (
              <BlockDes>
                <a href={`${STOREFRONT}/journal/${savedSlug}`} target="_blank" rel="noopener noreferrer">
                  View on site <Icon name="external" />
                </a>
              </BlockDes>
            )}
          </BlockHeadContent>
        </BlockHead>

        <Row className="g-4">
          <Col lg={8}>
            <Block>
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="form-group">
                    <label className="form-label">Title (H1)</label>
                    <input type="text" className="form-control form-control-lg" maxLength={200} value={form.title} onChange={set("title")} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Short summary (shown on the Journal list)</label>
                    <textarea className="form-control" rows={2} maxLength={500} value={form.excerpt} onChange={set("excerpt")} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Article</label>
                    <RichTextEditor
                      value={form.body}
                      resetKey={editorKey}
                      onChange={(body) => setForm((f) => ({ ...f, body }))}
                      minHeight={420}
                    />
                  </div>
                </div>
              </div>
            </Block>
          </Col>

          <Col lg={4}>
            <Block>
              <div className="card card-bordered mb-3">
                <div className="card-inner">
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <div className="input-group">
                      <span className="input-group-text fs-12px">/journal/</span>
                      <input type="text" className="form-control" maxLength={150} placeholder="from the title" value={form.slug} onChange={set("slug")} />
                    </div>
                    <div className="form-note mt-1">Changing it after publishing breaks old links.</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input type="text" className="form-control" maxLength={80} placeholder="e.g. Guides, Ingredients" value={form.category} onChange={set("category")} />
                  </div>
                  <ImageField label="Cover image" value={form.cover_image} onChange={(cover_image) => setForm((f) => ({ ...f, cover_image }))} />
                  <div className="form-group">
                    <label className="form-label">Publish date</label>
                    <input type="datetime-local" className="form-control" value={form.published_at} onChange={set("published_at")} />
                    <div className="form-note mt-1">Empty = now when you publish. A future date schedules it.</div>
                  </div>
                </div>
              </div>

              <div className="card card-bordered mb-3">
                <div className="card-inner">
                  <h6 className="title mb-3">Author & product</h6>
                  <div className="form-group">
                    <label className="form-label">Author name</label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={120}
                      placeholder="Empty = by ryzhm"
                      value={form.author_name}
                      onChange={set("author_name")}
                    />
                  </div>
                  <ImageField
                    label="Author photo"
                    hint="Square photo works best (shown as a small circle)."
                    value={form.author_image}
                    onChange={(author_image) => setForm((f) => ({ ...f, author_image }))}
                  />
                  <div className="form-group">
                    <label className="form-label">Featured product</label>
                    <select className="form-select form-control" value={form.product_id} onChange={set("product_id")}>
                      <option value="">None</option>
                      {products.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name}
                          {p.is_active ? "" : " (inactive — hidden on site)"}
                        </option>
                      ))}
                    </select>
                    <div className="form-note mt-1">Shown as a “Shop this fragrance” card at the end of the article.</div>
                  </div>
                </div>
              </div>

              <div className="card card-bordered mb-3">
                <div className="card-inner">
                  <h6 className="title mb-3">Google & sharing</h6>
                  <SeoFields
                    value={form}
                    onChange={(seo) => setForm((f) => ({ ...f, ...seo }))}
                    path={path}
                    fallbackTitle={form.title}
                    showKeywords={false}
                    showImage={false}
                  />
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button color="primary" disabled={saving} onClick={() => save(true)}>
                  {saving ? <Spinner size="sm" /> : form.is_published ? "Update" : "Publish"}
                </Button>
                <Button color="light" disabled={saving} onClick={() => save(false)}>
                  {form.is_published ? "Unpublish (draft)" : "Save draft"}
                </Button>
              </div>
            </Block>
          </Col>
        </Row>
      </Content>
    </React.Fragment>
  );
};

export default JournalEditor;
